import { DependencyList, EffectCallback, useEffect, useRef } from 'react';
import { isArray, isEqual, isUndefined } from 'lodash';

/**
 * Custom hook to use useEffect with deep comparison of dependencies array or object
 * @param fn - The callback function to execute when dependencies change
 * @param deps - The dependency list, which can be an array or a single value or object
 */
export default function useDeepEffect(fn: EffectCallback, deps: DependencyList | undefined) {
  // create two refs: one to track the initial render and another to store the previous dependencies
  const isFirst = useRef(true);
  const prevDeps = useRef(deps);
  const currentDeps = (index: number) => (isArray(deps) ? deps[index] : deps);

  // convert the dependencies to an array if it's not already an array
  const dependencies = isUndefined(deps) ? [] : isArray(deps) ? deps : [deps];
  useEffect(() => {
    const isFirstEffect = isFirst.current;
    // compare the previous dependencies with the current dependencies using a deep comparison method like isEqual from lodash.
    const prevDependencies = isArray(prevDeps.current) ? prevDeps.current : [prevDeps.current];
    const isSame =
      prevDeps?.current && deps
        ? prevDependencies.every((obj, index) => isEqual(obj, currentDeps(index)))
        : true;

    isFirst.current = false;
    prevDeps.current = deps;
    // if it's the first render or if the dependencies have changed, call the provided callback function.
    if (isFirstEffect || !isSame) {
      return fn();
    }
  }, dependencies);
}
